import React, { useCallback, useEffect, useRef, useState } from "react";
import { PaymentOptions, SplitInstallment } from "./PaymentOptions";

export type InitializePaymentFn = (payload: {
  email: string;
  name: string;
  amount: number;
  paymentPlan: "full" | "split";
  metadata?: Record<string, any>;
}) => Promise<any>;

export const handleInitializeResponseRedirect = (res: any): boolean => {
  const authUrl =
    res?.authorization_url ||
    res?.data?.authorization_url ||
    res?.url ||
    res?.data?.url ||
    res?.authorizationUrl ||
    res?.redirectUrl;
  if (authUrl) {
    window.location.href = authUrl;
    return true;
  }
  return false;
};

export interface WebinarPageProps {
  embedId?: string;
  initializePayment: InitializePaymentFn;
  fullPrice?: number;
  splitFirst?: number;
  /**
   * Structured installment schedule passed directly to PaymentOptions.
   * Preferred over the legacy splitSecond / splitThird scalars.
   */
  splitInstallments?: SplitInstallment[];
  /** @deprecated Use splitInstallments instead */
  splitSecond?: number;
  /** @deprecated Use splitInstallments instead */
  splitThird?: number;
  features?: string[];
  paymentTriggerSeconds?: number;
  courseName?: string;
  sourceTag?: string;
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
//  WebinarPage
// ─────────────────────────────────────────────────────────────────────────────

const WebinarPage: React.FC<WebinarPageProps> = ({
  embedId = "KxeXZXfJfok",
  initializePayment,
  fullPrice = 49999,
  splitFirst = 24999,
  splitInstallments,
  splitSecond,
  splitThird,
  features,
  paymentTriggerSeconds = 300,
  courseName = "Digital Income Accelerator",
  sourceTag = "webinar_5min",
  className = "",
}) => {
  // ── Resolve installment schedule once ───────────────────────────────────────
  // Prefer the structured splitInstallments prop; fall back to legacy scalars.
  const resolvedInstallments: SplitInstallment[] = (() => {
    if (splitInstallments && splitInstallments.length > 0)
      return splitInstallments;

    const installments: SplitInstallment[] = [];

    const second =
      splitSecond !== undefined
        ? splitSecond
        : Math.max(0, fullPrice - splitFirst);
    installments.push({ amount: second, dueLabel: "in 30 days" });

    if (splitThird !== undefined && splitThird > 0) {
      installments.push({ amount: splitThird, dueLabel: "in 60 days" });
    }

    return installments;
  })();

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [isInitializing, setIsInitializing] = useState(false);

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const plyrRef = useRef<any>(null);
  const progressIntervalRef = useRef<number>(-1);
  const paymentTriggeredRef = useRef(false);
  const resumeTimeRef = useRef(0);

  const SESSION_PROGRESS = `webinar_progress_${embedId}`;
  const SESSION_TRIGGERED = `webinar_payment_triggered_${embedId}`;

  // ─────────────────────────────────────────────────────────────────────────────
  //  Helpers
  // ─────────────────────────────────────────────────────────────────────────────

  const stopInterval = useCallback(() => {
    if (progressIntervalRef.current !== -1) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = -1;
    }
  }, []);

  const mutePlayer = useCallback((p: any) => {
    try {
      p.muted = true;
    } catch {}
    try {
      p.volume = 0;
    } catch {}
    try {
      p.embed?.mute?.();
    } catch {}
  }, []);

  const unmutePlayer = useCallback((p: any): boolean => {
    try {
      p.muted = false;
    } catch {}
    try {
      p.volume = 1;
    } catch {}
    try {
      p.embed?.unMute?.();
    } catch {}
    try {
      p.embed?.setVolume?.(100);
    } catch {}
    const ok =
      (typeof p.muted === "boolean" && p.muted === false) ||
      (typeof p.volume === "number" && p.volume > 0);
    return ok;
  }, []);

  const startInterval = useCallback(
    (p: any) => {
      stopInterval();
      const tick = () => {
        try {
          const now = Number(p.currentTime || 0);
          try {
            sessionStorage.setItem(SESSION_PROGRESS, String(now));
          } catch {}

          const raw = Number(p.duration) || Number(p.media?.duration) || 0;
          const dur = raw > 0 && isFinite(raw) ? raw : paymentTriggerSeconds;
          setCurrentProgress(Math.min((now / Math.max(1, dur)) * 100, 100));

          if (now >= paymentTriggerSeconds && !paymentTriggeredRef.current) {
            paymentTriggeredRef.current = true;
            setShowPayment(true);
            try {
              sessionStorage.setItem(SESSION_TRIGGERED, "true");
            } catch {}
          }
        } catch {}
      };
      tick();
      progressIntervalRef.current = window.setInterval(tick, 500);
    },
    [stopInterval, paymentTriggerSeconds, SESSION_PROGRESS, SESSION_TRIGGERED],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  //  Plyr bootstrap
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_PROGRESS);
      resumeTimeRef.current = raw ? Math.max(0, parseFloat(raw)) : 0;
    } catch {}

    try {
      if (
        sessionStorage.getItem(SESSION_TRIGGERED) === "true" &&
        resumeTimeRef.current >= paymentTriggerSeconds
      ) {
        paymentTriggeredRef.current = true;
        setShowPayment(true);
      }
    } catch {}

    let destroyed = false;
    let retryTimer: number;

    const init = () => {
      if (destroyed) return;
      if (!(window as any).Plyr) {
        retryTimer = window.setTimeout(init, 200);
        return;
      }
      if (!containerRef.current) return;

      const el = document.createElement("div");
      el.setAttribute("data-plyr-provider", "youtube");
      el.setAttribute("data-plyr-embed-id", embedId);
      containerRef.current.appendChild(el);

      const p = new (window as any).Plyr(el, {
        controls: [],
        clickToPlay: false,
        keyboard: { global: false, focused: false },
        hideControls: true,
        muted: true,
        autoplay: false,
        fullscreen: { enabled: false },
        youtube: {
          noCookie: true,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          autoplay: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
        },
      });

      plyrRef.current = p;

      p.on("ready", () => {
        if (destroyed) return;
        setIsVideoReady(true);
        mutePlayer(p);

        const resume = resumeTimeRef.current;
        const seekThenPlay = () => {
          if (destroyed) return;
          if (resume > 5) {
            try {
              p.currentTime = resume;
            } catch {}
            window.setTimeout(() => {
              if (!destroyed) playMuted();
            }, 600);
          } else {
            playMuted();
          }
        };
        window.setTimeout(seekThenPlay, 400);
      });

      p.on("playing", () => {
        if (!destroyed) startInterval(p);
      });

      p.on("pause", () => {
        if (destroyed) return;
        window.setTimeout(() => {
          if (destroyed) return;
          try {
            const pr = p.play();
            if (pr?.then) pr.catch(() => {});
          } catch {}
        }, 80);
      });

      p.on("volumechange", () => {
        if (destroyed) return;
        setIsMuted(p.muted === true || p.volume === 0);
      });
    };

    init();

    return () => {
      destroyed = true;
      clearTimeout(retryTimer);
      stopInterval();
      try {
        plyrRef.current?.destroy();
      } catch {}
      plyrRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedId]);

  const playMuted = useCallback(() => {
    const p = plyrRef.current;
    if (!p) return;
    mutePlayer(p);
    const attempt = (n: number) => {
      if (!plyrRef.current) return;
      const pr = p.play();
      if (pr && typeof pr.then === "function") {
        pr.then(() => startInterval(p)).catch(() => {
          try {
            p.embed?.mute?.();
            p.embed?.playVideo?.();
            startInterval(p);
          } catch {}
          if (n < 5) window.setTimeout(() => attempt(n + 1), 1500);
        });
      } else {
        startInterval(p);
      }
    };
    attempt(1);
  }, [mutePlayer, startInterval]);

  const userUnmute = useCallback(async () => {
    const p = plyrRef.current;
    if (!p) return;
    const ok = unmutePlayer(p);
    if (p.paused) {
      try {
        const pr = p.play();
        if (pr?.then) await pr;
      } catch {}
    }
    window.setTimeout(() => {
      if (p.muted === true || p.volume === 0) {
        alert(
          "Please click anywhere on the page first, then tap Unmute again.\n" +
            "Browsers require a direct interaction to enable sound.",
        );
      } else {
        setIsMuted(false);
      }
    }, 350);
    if (ok) setIsMuted(false);
  }, [unmutePlayer]);

  // ── Global keyboard blocker ──────────────────────────────────────────────────
  useEffect(() => {
    const BLOCKED = new Set([
      " ",
      "k",
      "K",
      "MediaPlayPause",
      "MediaStop",
      "ArrowLeft",
      "ArrowRight",
      "j",
      "J",
      "l",
      "L",
    ]);
    const onKey = (e: KeyboardEvent) => {
      if (BLOCKED.has(e.key ?? "")) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  useEffect(() => () => stopInterval(), [stopInterval]);

  // ─────────────────────────────────────────────────────────────────────────────
  //  Payment submit
  //
  //  PaymentOptions owns the form (name, email, phone) and resolves the exact
  //  amount for the chosen plan before calling onSubmit. We simply forward the
  //  payload — no need to re-calculate the amount here.
  //  isInitializing is passed back as the isSubmitting prop so PaymentOptions
  //  locks its form and button while the API call is in flight.
  // ─────────────────────────────────────────────────────────────────────────────
  // inside WebinarPage component — replace the previous handlePaymentSubmit
  const handlePaymentSubmit = async (payload: {
    name: string;
    email: string;
    phone: string;
    paymentPlan: "full" | "split";
    amount: number; // (ignored — we compute amount here the same way as DBCBuyPage)
  }) => {
    try {
      setIsInitializing(true);

      // mirror DBCBuyPage logic: choose the plan amount (expected in kobo) then convert to Naira
      const toBePaid = payload.paymentPlan === "full" ? fullPrice : splitFirst;
      const numericAmount = toBePaid / 100; // kobo -> Naira (frontend sends Naira integer)

      const res = await initializePayment({
        email: payload.email,
        name: payload.name,
        amount: numericAmount,
        paymentPlan: payload.paymentPlan,
        metadata: {
          phone: payload.phone,
          course: courseName,
          source: sourceTag,
        },
      });

      if (!handleInitializeResponseRedirect(res)) {
        alert(
          (res as any)?.message ??
            "Payment initialized but no redirect URL was returned.",
        );
      }
    } catch (err: any) {
      alert(
        err?.data?.message ||
          err?.message ||
          "Something went wrong while starting payment.",
      );
    } finally {
      setIsInitializing(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-gray-900 ${className}`}
    >
      {/* ── Header ── */}
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-6 md:py-8 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight">
            {courseName}
          </h1>
          <p className="text-blue-100 mt-1 md:mt-2 text-sm md:text-lg">
            Transform Your Skills Into Sustainable Online Income
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* ════ Left column: video + enrollment ════ */}
          <div className="lg:col-span-2">
            {/* ── Video shell ── */}
            <div
              className="rounded-xl overflow-hidden bg-black shadow-2xl relative"
              style={{ paddingBottom: "56.25%", height: 0 }}
            >
              {/* Loading skeleton */}
              {!isVideoReady && (
                <div className="absolute inset-0 z-30 flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black">
                  <div className="text-center space-y-6 px-4">
                    <div className="relative w-16 h-16 md:w-20 md:h-20 mx-auto">
                      <div className="absolute inset-0 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                      <div
                        className="absolute inset-0 border-4 border-blue-400/20 border-t-transparent rounded-full animate-spin"
                        style={{
                          animationDirection: "reverse",
                          animationDuration: "1.5s",
                        }}
                      />
                    </div>
                    <p className="text-white/90 text-base md:text-lg font-semibold">
                      Loading video...
                    </p>
                    <p className="text-white/60 text-xs md:text-sm">
                      Preparing your experience
                    </p>
                  </div>
                </div>
              )}

              {/* Plyr mount point — pointer-events: none lets the overlay above intercept clicks */}
              <div
                ref={containerRef}
                className="absolute inset-0 z-10"
                style={{
                  opacity: isVideoReady ? 1 : 0,
                  visibility: isVideoReady ? "visible" : "hidden",
                  transition: "opacity 0.5s ease-in-out",
                  pointerEvents: "none",
                }}
              />

              {/* Full-coverage transparent overlay — blocks all user interaction with the player */}
              {isVideoReady && (
                <div
                  aria-hidden
                  className="absolute inset-0 z-20"
                  style={{ cursor: "default", background: "transparent" }}
                  onClick={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                  onDoubleClick={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                />
              )}

              {/* Unmute button — the only user-visible control, sits above the overlay */}
              {isVideoReady && isMuted && (
                <div className="absolute top-3 right-3 md:top-4 md:right-4 z-50">
                  <button
                    onClick={userUnmute}
                    className="flex items-center gap-2 bg-black/80 backdrop-blur-sm text-white border border-white/20 px-4 py-2.5 rounded-lg font-semibold text-sm shadow-xl hover:bg-black/90 hover:border-white/40 transition-all duration-200 active:scale-95"
                    aria-label="Unmute video"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <line x1="23" y1="9" x2="17" y2="15" />
                      <line x1="17" y1="9" x2="23" y2="15" />
                    </svg>
                    Tap to Unmute
                  </button>
                </div>
              )}
            </div>

            {/* ── Enrollment section — slides in after paymentTriggerSeconds ── */}
            {showPayment && (
              <div className="mt-6 md:mt-8 bg-white rounded-2xl p-4 md:p-6 shadow-xl border border-gray-200">
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                    ✓ Special Offer Unlocked
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                  Enrollment Options
                </h3>
                <p className="text-xs md:text-sm text-gray-600 mb-4 md:mb-6">
                  Choose your plan and start your journey today
                </p>

                <PaymentOptions
                  fullPrice={fullPrice}
                  splitFirst={splitFirst}
                  splitInstallments={resolvedInstallments}
                  features={features}
                  initialSelected="full"
                  onSelect={() => {}}
                  onSubmit={handlePaymentSubmit}
                  isSubmitting={isInitializing}
                />

                {isInitializing && (
                  <p className="text-xs text-gray-500 mt-4 text-center">
                    Initializing secure payment…
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ════ Sidebar: progress ════ */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-lg border border-gray-200">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                Watch & Learn
              </h3>
              <p className="text-xs md:text-sm text-gray-600 mb-4 md:mb-6">
                {showPayment
                  ? "Continue watching or enroll below"
                  : "Complete the webinar to unlock enrollment options"}
              </p>

              <div className="space-y-4">
                {/* Progress bar */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-gray-700">
                      Progress
                    </span>
                    <span className="text-xs text-gray-500">
                      {Math.floor(currentProgress)}% complete
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 md:h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-blue-500 h-full rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${currentProgress}%` }}
                    />
                  </div>
                </div>

                {!showPayment ? (
                  <div className="bg-blue-50 rounded-lg p-3 md:p-4 border border-blue-100">
                    <p className="text-xs md:text-sm text-blue-900 font-medium">
                      🎯 Keep watching to unlock special enrollment pricing
                    </p>
                  </div>
                ) : (
                  <div className="bg-green-50 rounded-lg p-3 md:p-4 border border-green-100">
                    <p className="text-xs md:text-sm text-green-900 font-medium">
                      ✅ Enrollment unlocked! Scroll down to choose your plan.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>

        {/* What you'll get */}
        <div className="mt-8 md:mt-12 bg-white rounded-2xl p-6 md:p-8 shadow-lg">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
            What You'll Get in This Program
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {features &&
              features.map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
                    <span className="text-blue-600 text-xs md:text-sm">✓</span>
                  </div>
                  <p className="text-xs md:text-sm text-gray-700">{f}</p>
                </div>
              ))}
          </div>
        </div>
      </main>

      <footer className="bg-white py-6 md:py-8 mt-8 md:mt-12 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-4 md:px-6 text-center text-xs md:text-sm text-gray-600">
          © {new Date().getFullYear()} D.B.C. Formula. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default WebinarPage;
